import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TokenService } from 'src/app/core/token/token.service';
import { HttpClient } from '@angular/common/http';
import { VisualizadorArquivosComponent } from '../modais/visualizador-arquivos/visualizador-arquivos.component';
import { ChamadosService } from 'src/app/services/chamados.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-campo-anexo',
  templateUrl: './campo-anexo.component.html',
  styleUrls: ['./campo-anexo.component.scss']
})
export class CampoAnexoComponent implements OnInit, OnDestroy {
  @Input() chamadoId: number;
  @Input() campoId: number;
  @Input() campoLabel: string;
  
  fileBlob: Blob = null;
  
  private _valor: any;
  @Input() set valor(val: any) {
    if (val !== this._valor) {
      this._valor = val;
      if (val) {
        try {
          if (typeof val === 'string') {
            this.anexo = JSON.parse(val);
          } else if (typeof val === 'object' && val.nome_original) {
            this.anexo = val;
          } else {
            this.anexo = val;
          }
        } catch (error) {
          console.error('🔧 CAMPO ANEXO - Erro ao processar valor no setter:', error);
          this.anexo = null;
        }
      } else {
        this.anexo = null;
      }
    }
  }
  
  get valor(): any {
    return this._valor;
  }
  @Input() obrigatorio: boolean = false;
  @Input() desabilitado: boolean = false;
  @Input() preview: boolean = false;
  @Input() showTooltip: boolean = true;
  @Input() mostrarErro: boolean = false;
  @Output() valorChange = new EventEmitter<any>();
  @Output() fileUploaded = new EventEmitter<any>();

  anexo: any = null;
  uploading: boolean = false;
  newSubs: Subscription;

  readonly EXTENSOES_PERMITIDAS = [
    '.png', '.gif', '.jpeg', '.jpg', '.rtf', '.pdf', '.doc', '.docx', '.txt',
    '.xls', '.xlsx', '.xml', '.json', '.msg', '.zip', '.rar', '.csv', '.ret',
    '.rem', '.otp', '.odf', '.odb', '.oxt', '.pptx', '.ppt', '.jfif', '.7z',
    '.pfx', '.mp3', '.mp4', '.mpeg', '.wav'
  ];
  
  readonly TAMANHO_MAXIMO = 50 * 1024 * 1024; // 50MB

  constructor(
    private translate: TranslateService,
    private tokenService: TokenService,
    private modalService: NgbModal,
    private http: HttpClient,
    private chamadosService: ChamadosService
  ) { }

  ngOnInit(): void {
    if (!this.anexo && this.valor) {
      try {
        if (typeof this.valor === 'string') {
          this.anexo = JSON.parse(this.valor);
        } else if (typeof this.valor === 'object' && this.valor.nome_original) {
          this.anexo = this.valor;
        } else {
          this.anexo = this.valor;
        }
      } catch (error) {
        console.error('🔧 CAMPO ANEXO - Erro ao processar anexo no ngOnInit:', error);
        this.anexo = null;
      }
    }
  }

  ngOnDestroy(): void {
    this.newSubs?.unsubscribe();
  }

  getSelectedFile(): File | null {
    return this.fileBlob as File;
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    const extensao = '.' + file.name.split('.').pop().toLowerCase();
    if (!this.EXTENSOES_PERMITIDAS.includes(extensao)) {
      event.target.value = '';
      return;
    }

    if (file.size > this.TAMANHO_MAXIMO) {
      event.target.value = '';
      return;
    }

    if (this.preview) {
      this.fileBlob = file;
      this.anexo = {
        nome_original: file.name,
        tamanho_bytes: file.size,
        tipo: file.type,
        url: '#'
      };
      this.valorChange.emit(JSON.stringify(this.anexo));
      this.fileUploaded.emit({ anexo: this.anexo, file: file });
    } else {
      this.uploadFile(file);
    }

    event.target.value = '';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    if (this.desabilitado || this.uploading) return;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const fakeEvent = { target: { files: files } };
      this.onFileSelected(fakeEvent);
    }
  }

  uploadFile(file: File): void {
    if (!this.chamadoId || !this.campoId) {
      return;
    }

    this.uploading = true;
    const formData = new FormData();
    formData.append('arquivo', file);

    const token = this.tokenService.getToken();
    const uploadUrl = `${environment.apiGestorUrlLocal}api/campos-personalizados/anexo/upload/${this.chamadoId}/${this.campoId}`;
    
    fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'authorization': token,
        'session': this.tokenService.getSessionUsuario()
      },
      body: formData
    })
    .then(response => {
      return response.json();
    })
    .then(result => {
      this.uploading = false;
      
      const isSuccess = result.success === true || 
                       result.success === 'true' ||
                       result.data ||
                       result.anexo ||
                       result.status_code === 200 ||
                       (result.success === undefined && !result.error_msg && !result.error && !result.message);
      
      if (isSuccess) {
        let anexoData;
        if (result.data) {
          anexoData = result.data;
        } else if (result.anexo) {
          anexoData = result.anexo;
        } else if (result.nome_original) {
          anexoData = result;
        } else {
          anexoData = result;
        }
        
        this.anexo = anexoData;
        this.fileBlob = file;
        
        this.valorChange.emit(JSON.stringify(this.anexo));
        this.fileUploaded.emit({ anexo: this.anexo, file: file });
      }
    })
    .catch(error => {
      this.uploading = false;
    });
  }

  deleteAnexo(): void {
    Swal.fire({
      title: this.translate.instant('CASO_DELETE_ARQUIVO_NAO_RECUPERAR'),
      icon: 'error',
      focusConfirm: true,
      showCancelButton: true,
      confirmButtonText: this.translate.instant('SIM'),
      cancelButtonText: this.translate.instant('NAO'),
    }).then((result) => {
      if (result.isConfirmed) {
        this.executarDelete();
      }
    });
  }

  private executarDelete(): void {
    if (this.preview) {
      this.anexo = null;
      this.fileBlob = null;
      this.valorChange.emit(null);
      return;
    }

    if (!this.chamadoId || !this.campoId) {
      return;
    }

    const token = this.tokenService.getToken();
    const deleteUrl = `${environment.apiGestorUrlLocal}api/campos-personalizados/anexo/delete/${this.chamadoId}/${this.campoId}`;
    
    fetch(deleteUrl, {
      method: 'DELETE',
      headers: {
        'authorization': token,
        'session': this.tokenService.getSessionUsuario(),
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.json();
    })
    .then(result => {
      const isSuccess = result.success === true || 
                       result.success === 'true' ||
                       (result.success === undefined && !result.error_msg && !result.error && !result.message) ||
                       result.status_code === 200;
      
      if (isSuccess) {
        this.criarLogTicketAlterado(this.anexo?.nome_original || 'arquivo');
        
        this.anexo = null;
        this.fileBlob = null;
        this.valorChange.emit(null);
      }
    })
    .catch(error => {
    });
  }

  viewFile(): void {
    if (!this.anexo) {
      return;
    }

    let fileUrl: string;
    let isBlob = false;

    if (this.fileBlob) {
      fileUrl = URL.createObjectURL(this.fileBlob);
      isBlob = true;
    } 
    else if (this.anexo.url && this.anexo.url !== '#' && this.anexo.url.startsWith('http')) {
      fileUrl = this.anexo.url;
    }
    else if (this.chamadoId && this.campoId) {
      const token = this.tokenService.getToken();
      fileUrl = `${environment.apiGestorUrlLocal}api/campos-personalizados/anexo/download/${this.chamadoId}/${this.campoId}?token=${token}`;
    }
    else {
      return;
    }

    const nomeArquivo = this.anexo.nome_original || '';
    const isImage = nomeArquivo.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i);
    const isAudio = nomeArquivo.match(/\.(mp3|wav|ogg)$/i);
    const isVideo = nomeArquivo.match(/\.(mp4|webm|ogg|mov)$/i);
    const isPdf = nomeArquivo.match(/\.pdf$/i);

    if (isImage || isAudio || isVideo || isPdf) {
      const modalRef = this.modalService.open(VisualizadorArquivosComponent, {
        scrollable: false,
        windowClass: 'modal-custom-arquivos',
        size: 'grande',
        keyboard: true,
      });

      if (isBlob) {
        const extensao = nomeArquivo.split('.').pop()?.toLowerCase();
        const urlComExtensao = fileUrl + '#.' + extensao;
        modalRef.componentInstance.fileUrl = urlComExtensao;
        
        modalRef.result.finally(() => {
          URL.revokeObjectURL(fileUrl);
        });
      } else {
        modalRef.componentInstance.fileUrl = fileUrl;
      }
      
    } else {
      this.baixarArquivo(fileUrl);
      
      if (isBlob) {
        setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
      }
    }
  }

  private baixarArquivo(fileUrl: string): void {
    if (this.fileBlob) {
      const url = URL.createObjectURL(this.fileBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = this.anexo.nome_original || 'arquivo';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      fetch(fileUrl)
        .then(response => response.blob())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = this.anexo.nome_original || 'arquivo';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        })
        .catch(error => {
        });
    }
  }

  private criarLogTicketAlterado(nomeArquivo: string): void {
    if (!this.chamadoId) {
      return;
    }

    const mensagem = `Arquivo ${nomeArquivo} foi deletado do Campo ${this.campoLabel || 'Anexo'}.`;
    
    const logData = {
      chamadoLog: {
        observacao: mensagem,
        observacao_html: mensagem,
        chamado_id: this.chamadoId,
        log_tipo_id: 7
      }
    };

    this.chamadosService.criarLog(logData).subscribe({
      next: (result) => {
      },
      error: (error) => {
      }
    });
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'Kb', 'Mb', 'Gb'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const size = parseFloat((bytes / Math.pow(k, i)).toFixed(1));
    return size + ' ' + sizes[i];
  }
}
